import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";

import { HEADLINE, META } from "@/lib/typography";
import { cn } from "@/lib/utils";

interface DrawerRootProps extends React.ComponentProps<typeof DrawerPrimitive.Root> {
  shouldScaleBackground?: boolean;
}

const Drawer = ({ shouldScaleBackground = true, repositionInputs = false, ...props }: DrawerRootProps) => (
  <DrawerPrimitive.Root shouldScaleBackground={shouldScaleBackground} repositionInputs={repositionInputs} {...props} />
);
Drawer.displayName = "Drawer";

const DrawerTrigger = DrawerPrimitive.Trigger;

const DrawerPortal = DrawerPrimitive.Portal;

const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, onPointerDown, ...props }, ref) => (
  <DrawerPrimitive.Overlay
    ref={ref}
    className={cn("fixed inset-0 z-[52] bg-black/70", className)}
    onPointerDown={(e) => {
      // When the overlay is clicked to dismiss the drawer, the overlay unmounts
      // before pointerup fires. The browser then synthesizes a click on whatever
      // is underneath (e.g. a <Link>), causing unwanted navigation.
      // Fix: absorb the very next click — but only if the overlay has unmounted,
      // so we never block the dismiss event itself.
      const overlayEl = e.currentTarget as HTMLElement;
      const absorb = (ce: MouseEvent) => {
        if (!overlayEl.isConnected) {
          ce.stopPropagation();
          ce.preventDefault();
        }
      };
      document.addEventListener("click", absorb, { capture: true, once: true });
      setTimeout(() => document.removeEventListener("click", absorb, true), 500);
      onPointerDown?.(e);
    }}
    {...props}
  />
));
DrawerOverlay.displayName = DrawerPrimitive.Overlay.displayName;

interface DrawerContentProps extends React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content> {
  containerClassName?: string;
  handleClassName?: string;
  withHandle?: boolean;
  /** Disable the inner scroll wrapper — use when children manage their own scroll */
  disableInnerScroll?: boolean;
}

const DrawerContent = React.forwardRef<React.ElementRef<typeof DrawerPrimitive.Content>, DrawerContentProps>(
  ({ className, children, containerClassName, handleClassName, withHandle = true, disableInnerScroll = false, ...props }, ref) => (
    <DrawerPortal>
      <DrawerOverlay />
      {/* stopPropagation prevents React synthetic events from bubbling
          through the portal to parent components (e.g. a <Link> card) */}
      <div
        className={cn("sheet-position fixed inset-x-0 z-[52] flex justify-center pointer-events-none transition-[bottom] duration-200", containerClassName)}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <DrawerPrimitive.Content
          ref={ref}
          className={cn(
            "sheet-surface pointer-events-auto relative mt-10 flex w-full max-w-[var(--sheet-width)] flex-col rounded-t-lg border ui-surface px-4 pb-6 pt-4 sm:px-6 lg:px-8 focus:outline-none focus-visible:outline-none",
            className,
          )}
          aria-describedby={props["aria-describedby"] || "drawer-description"}
          {...props}
        >
          {withHandle ? <div className={cn("mx-auto mb-4 h-1.5 w-16 rounded-full bg-muted-foreground/30", handleClassName)} /> : null}
          <div className={cn("min-h-0 flex-1 -mx-2 px-2 pb-safe", disableInnerScroll ? "overflow-hidden flex flex-col" : "overflow-y-auto")}>
            {children}
          </div>
        </DrawerPrimitive.Content>
      </div>
    </DrawerPortal>
  ),
);
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)} {...props} />
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("mt-auto flex flex-col gap-2 p-4", className)} {...props} />
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Title
    ref={ref}
    className={cn(HEADLINE, "tracking-tight", className)}
    {...props}
  />
));
DrawerTitle.displayName = DrawerPrimitive.Title.displayName;

const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DrawerPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DrawerPrimitive.Description ref={ref} className={cn(META, className)} {...props} />
));
DrawerDescription.displayName = DrawerPrimitive.Description.displayName;

export {
  Drawer,
  DrawerPortal,
  DrawerOverlay,
  DrawerTrigger,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
};
